import React, { useState } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import toast from 'react-hot-toast';
import { useMessagesData } from '../hooks/useMessagesData';
import MessagesSidebar from '../components/messages/MessagesSidebar';
import MessagesStatsBar from '../components/messages/MessagesStatsBar';
import MessagesList from '../components/messages/MessagesList';
import ComposeModal from '../components/messages/ComposeModal';

const EMPTY_MESSAGE = {
  tytul: '', tresc: '', odbiorca_login: '',
  odbiorca_location_id: '', is_global: false, priorytet: 'normalny'
};

export default function MessagesPage() {
  const { hasPermission } = usePermissions();
  const canSendMessages = hasPermission('wiadomosci', 'wysylanie');
  const canDeleteMessages = hasPermission('wiadomosci', 'usuwanie');

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [newMessage, setNewMessage] = useState(EMPTY_MESSAGE);

  const {
    stats, loading, selectedFolder, setSelectedFolder,
    searchQuery, setSearchQuery, page, setPage,
    pageCount, paginatedMessages, users, locations,
    authUser, isAuthenticated, isAdmin,
    handleMarkAsRead, handleSendMessage, handleDeleteMessage,
  } = useMessagesData();

  const handleSelectMessage = async (message) => {
    if (selectedMessage?.id === message.id) { setSelectedMessage(null); return; }
    setSelectedMessage(message);
    await handleMarkAsRead(message);
  };

  const handleReply = (message) => {
    setReplyTo(message);
    setNewMessage({ tytul: `Re: ${message.tytul}`, tresc: '', odbiorca_login: message.nadawca_login, odbiorca_location_id: '', is_global: false, priorytet: 'normalny' });
    setShowComposeModal(true);
  };

  const handleDelete = async (messageId) => {
    const ok = await handleDeleteMessage(messageId);
    if (ok) setSelectedMessage(null);
  };

  const handleSend = async () => {
    if (!newMessage.tytul.trim() || !newMessage.tresc.trim()) { toast.error('Wypelnij tytul i tresc wiadomosci'); return; }
    if (!newMessage.is_global && !newMessage.odbiorca_login && !newMessage.odbiorca_location_id) { toast.error('Wybierz odbiorce lub zaznacz "Do wszystkich"'); return; }
    const ok = await handleSendMessage(newMessage);
    if (ok) { setShowComposeModal(false); setReplyTo(null); setNewMessage(EMPTY_MESSAGE); }
  };

  const openCompose = () => { setReplyTo(null); setNewMessage(EMPTY_MESSAGE); setShowComposeModal(true); };
  const closeCompose = () => { setShowComposeModal(false); setReplyTo(null); };

  if (!loading && !isAuthenticated) {
    return (
      <div style={{ padding: '40px 20px' }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', backgroundColor: "var(--card-bg)", borderRadius: '12px', padding: '40px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '20px', color: 'var(--text)', marginBottom: '10px' }}>Brak dostepu</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '20px' }}>Musisz byc zalogowany aby korzystac z wiadomosci.</p>
          <a href="/login" style={{ display: 'inline-block', padding: '12px 24px', border: '1px solid var(--info)', color: 'var(--info)', textDecoration: 'none', borderRadius: '6px', fontWeight: 500 }}>
            Zaloguj sie
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Wiadomosci</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{authUser?.login ? `Uzytkownik: ${authUser.login}` : 'System wiadomosci wewnetrznych'}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canSendMessages && (
              <button className="btn btn-primary" onClick={openCompose}>
                Nowa wiadomosc
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <MessagesStatsBar stats={stats} selectedFolder={selectedFolder} setSelectedFolder={setSelectedFolder} />

        <div style={{ display: 'flex', gap: '16px' }}>
          <MessagesSidebar selectedFolder={selectedFolder} setSelectedFolder={setSelectedFolder} stats={stats} isAdmin={isAdmin} />

          <div style={{ flex: 1 }}>
            {/* Search */}
            <div style={{ backgroundColor: "var(--card-bg)", borderRadius: '6px', border: '1px solid var(--border)', padding: '10px', marginBottom: '12px' }}>
              <input
                type="text" placeholder="Szukaj wiadomosci..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            <MessagesList
              loading={loading}
              paginatedMessages={paginatedMessages}
              page={page} pageCount={pageCount} setPage={setPage}
              selectedMessage={selectedMessage}
              selectedFolder={selectedFolder}
              isAdmin={isAdmin} authUser={authUser}
              canSendMessages={canSendMessages} canDeleteMessages={canDeleteMessages}
              onSelectMessage={handleSelectMessage}
              onReply={handleReply}
              onDelete={handleDelete}
              onCloseMessage={() => setSelectedMessage(null)}
              onCompose={openCompose}
            />
          </div>
        </div>
      </div>

      <ComposeModal
        isOpen={showComposeModal} onClose={closeCompose}
        replyTo={replyTo} newMessage={newMessage} setNewMessage={setNewMessage}
        users={users} locations={locations} onSend={handleSend}
      />
    </div>
  );
}
